import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';
import CareersHero from '../components/careers/CareersHero';
import CareersBenefits from '../components/careers/CareersBenefits';
import CareersOpenRoles from '../components/careers/CareersOpenRoles';
import CareersApplicationForm from '../components/careers/CareersApplicationForm';
import { axiosErrorMessage } from '../lib/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_EMPLOYER = {
  name: '', address: '', telephone: '', date_started: '', starting_title: '',
  starting_salary: '', ending_title: '', date_left: '', ending_salary: '',
  last_supervisor: '', reason_for_leaving: '', job_duties: '',
};
const EMPTY_REFERENCE = { name: '', ref_type: '', address: '', telephone: '', how_known: '' };

const INITIAL_FORM = {
  position_applied_for: '',
  first_name: '', last_name: '',
  home_telephone: '', work_telephone: '',
  address_street: '', address_line2: '', address_city: '', address_state: '',
  address_zip: '', address_country: 'United States',
  email: '',
  availability: [],
  at_least_18: null, immigration_prohibited: null, convicted_felony: null,
  felony_explanation: '',
  employers: [{ ...EMPTY_EMPLOYER }, { ...EMPTY_EMPLOYER }, { ...EMPTY_EMPLOYER }],
  skills: '',
  high_school: '', high_school_graduate: null, high_school_course: '',
  college1: '', college1_graduate: null, college1_course: '', college1_degree: '',
  college2: '', college2_graduate: null, college2_course: '', college2_degree: '',
  grad_school: '', grad_school_graduate: null, grad_school_course: '', grad_school_degree: '',
  additional_education: '', other_interests: '',
  references: [{ ...EMPTY_REFERENCE }, { ...EMPTY_REFERENCE }, { ...EMPTY_REFERENCE }],
  certification_name: '',
  resume_path: '', resume_filename: '', resume_content_type: '',
  honeypot: '',
};

export default function CareersPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEmployerChange = useCallback((index, field, value) => {
    setForm((prev) => {
      const employers = prev.employers.map((emp, i) => (i === index ? { ...emp, [field]: value } : emp));
      return { ...prev, employers };
    });
  }, []);

  const handleReferenceChange = useCallback((index, field, value) => {
    setForm((prev) => {
      const references = prev.references.map((ref, i) => (i === index ? { ...ref, [field]: value } : ref));
      return { ...prev, references };
    });
  }, []);

  const handleToggleAvailability = useCallback((value) => {
    setForm((prev) => {
      const has = prev.availability.includes(value);
      const availability = has
        ? prev.availability.filter((v) => v !== value)
        : [...prev.availability, value];
      return { ...prev, availability };
    });
  }, []);

  const handleResumeUpload = useCallback(async (file) => {
    setResumeUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/careers/resume`, fd);
      setForm((prev) => ({
        ...prev,
        resume_path: data.resume_path,
        resume_filename: data.resume_filename,
        resume_content_type: data.resume_content_type,
      }));
      toast.success('Resume uploaded', { description: data.resume_filename });
    } catch (error) {
      toast.error('Upload failed', { description: axiosErrorMessage(error, 'Resume upload failed.') });
    } finally {
      setResumeUploading(false);
    }
  }, []);

  const handleClearResume = useCallback(() => {
    setForm((prev) => ({ ...prev, resume_path: '', resume_filename: '', resume_content_type: '' }));
  }, []);

  const handleApplyClick = useCallback((roleTitle) => {
    handleChange('position_applied_for', roleTitle);
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  }, [handleChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (form.availability.length === 0) {
      toast.error('Please select your availability to work.');
      return;
    }
    if (form.at_least_18 === null || form.immigration_prohibited === null || form.convicted_felony === null) {
      toast.error('Please answer all Yes/No eligibility questions.');
      return;
    }
    if (form.high_school_graduate === null) {
      toast.error('Please indicate whether you graduated high school.');
      return;
    }
    for (let i = 0; i < form.references.length; i += 1) {
      if (!form.references[i].ref_type) {
        toast.error(`Please mark reference ${i + 1} as Personal or Business.`);
        return;
      }
    }
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/careers/apply`, form);
      if (response.data.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.success('Application submitted!', {
          description: "We've received your application — we'll be in touch soon.",
        });
      }
    } catch (error) {
      const msg = axiosErrorMessage(error, 'Something went wrong. Please try again.');
      toast.error('Failed to submit application', { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" tabIndex={-1}>
      <CareersHero />
      <CareersBenefits />
      <CareersOpenRoles onApplyClick={handleApplyClick} />
      <CareersApplicationForm
        form={form}
        onChange={handleChange}
        onEmployerChange={handleEmployerChange}
        onReferenceChange={handleReferenceChange}
        onToggleAvailability={handleToggleAvailability}
        onResumeUpload={handleResumeUpload}
        onClearResume={handleClearResume}
        resumeUploading={resumeUploading}
        onSubmit={handleSubmit}
        loading={loading}
        submitted={submitted}
      />
      </main>
      <Footer />
    </div>
  );
}
